import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { motion, useInView } from 'framer-motion';
import styled from 'styled-components';
import sr from '@utils/sr';
import { srConfig } from '@config';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';

const StyledSection = styled(motion.section)`
  max-width: 1600px;
  margin: 0 auto;
  padding: var(--space-5xl) var(--container-padding);
  position: relative;

  /* Background text */
  &::before {
    content: 'PROJECTS';
    position: absolute;
    top: 5%;
    left: 5%;
    font-size: clamp(150px, 25vw, 350px);
    font-weight: 700;
    color: rgba(0, 0, 0, 0.02);
    letter-spacing: 0.05em;
    z-index: 0;
    pointer-events: none;
    font-family: var(--font-display);
    text-transform: uppercase;
  }

  .section-header {
    margin-bottom: var(--space-4xl);
    position: relative;
    z-index: 1;
  }
`;

const StyledProjectsGrid = styled.ul`
  ${({ theme }) => theme.mixins.resetList};
  display: grid;
  gap: var(--space-4xl);
  position: relative;
  z-index: 1;
`;

const StyledProject = styled(motion.li)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4xl);
  align-items: center;
  position: relative;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: var(--space-xl);
  }

  &:nth-child(even) {
    .project-content {
      order: 2;
    }
    .project-image {
      order: 1;
    }

    @media (max-width: 968px) {
      .project-content {
        order: 1;
      }
      .project-image {
        order: 2;
      }
    }
  }

  &:not(:last-child) {
    padding-bottom: var(--space-4xl);
    border-bottom: 1px solid var(--border-color);
  }
`;

const StyledContent = styled(motion.div)`
  .project-overline {
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: var(--space-md);
    opacity: 0.7;
  }

  .project-title {
    font-size: clamp(32px, 5vw, 56px);
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: -0.02em;
    margin-bottom: var(--space-lg);
    color: var(--text-primary);
    font-family: var(--font-display);

    a {
      color: inherit;
      transition: var(--transition-base);

      &:hover {
        opacity: 0.7;
      }
    }
  }

  .project-description {
    font-size: clamp(16px, 1.8vw, 20px);
    line-height: 1.8;
    color: var(--text-tertiary);
    margin-bottom: var(--space-xl);
    opacity: 0.85;
    font-weight: 300;
  }

  .project-tech-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-md);
    margin-bottom: var(--space-xl);
    list-style: none;
    padding: 0;

    li {
      font-family: var(--font-mono);
      font-size: var(--fz-xs);
      color: var(--text-secondary);
      padding: var(--space-xs) var(--space-md);
      background: var(--bg-elevated);
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius);
      opacity: 0.8;
    }
  }

  .project-links {
    display: flex;
    gap: var(--space-md);
    align-items: center;

    a {
      ${({ theme }) => theme.mixins.flexCenter};
      width: 40px;
      height: 40px;
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius);
      color: var(--text-secondary);
      transition: var(--transition-base);

      &:hover {
        border-color: var(--text-primary);
        color: var(--text-primary);
        transform: translateY(-2px);
      }

      svg {
        width: 20px;
        height: 20px;
      }
    }
  }
`;

const StyledImage = styled(motion.div)`
  position: relative;
  aspect-ratio: 16/10;
  overflow: hidden;
  border-radius: var(--border-radius);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);

  /* Subtle overlay - Japanese minimal */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0.05) 0%,
      rgba(0, 0, 0, 0.02) 100%
    );
    mix-blend-mode: multiply;
    z-index: 2;
    pointer-events: none;
    transition: var(--transition-base);
  }

  a {
    display: block;
    width: 100%;
    height: 100%;

    &:hover {
      &::before {
        opacity: 0.5;
      }

      .img {
        transform: scale(1.05);
        filter: grayscale(20%) contrast(1.05);
      }
    }
  }

  .img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: grayscale(30%) contrast(1.05);
    transition: all var(--transition-slow);
  }
`;

const Featured = () => {
  const data = useStaticQuery(graphql`
    {
      featured: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/featured/" } }
        sort: { fields: [frontmatter___date], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              title
              cover {
                childImageSharp {
                  gatsbyImageData(width: 1200, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
              }
              tech
              github
              external
            }
            html
          }
        }
      }
    }
  `);

  const featuredProjects = data.featured.edges.filter(({ node }) => node);
  const revealTitle = useRef(null);
  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();
  const titleInView = useInView(revealTitle, { once: true, margin: '-100px' });

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const projectVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.6, -0.05, 0.01, 0.99],
      },
    },
  };

  return (
    <StyledSection
      id="projects"
      initial="hidden"
      animate={titleInView ? 'visible' : 'hidden'}
      variants={containerVariants}>
      <motion.div className="section-header" ref={revealTitle} variants={projectVariants}>
        <h2 className="numbered-heading">Featured Projects</h2>
      </motion.div>

      <StyledProjectsGrid>
        {featuredProjects &&
          featuredProjects.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { external, title, tech, github, cover } = frontmatter;
            const image = getImage(cover);
            const projectRef = el => (revealProjects.current[i] = el);

            return (
              <StyledProject
                key={i}
                ref={projectRef}
                variants={projectVariants}
                whileHover={{ y: -5 }}
                transition={{ type: 'spring', stiffness: 300 }}>
                <StyledContent className="project-content">
                  <div>
                    <p className="project-overline">Featured Project</p>

                    <h3 className="project-title">
                      <a href={external || github || '#'}>{title}</a>
                    </h3>

                    <div
                      className="project-description"
                      dangerouslySetInnerHTML={{ __html: html }}
                    />

                    {tech && tech.length > 0 && (
                      <ul className="project-tech-list">
                        {tech.map((techItem, idx) => (
                          <li key={idx}>{techItem}</li>
                        ))}
                      </ul>
                    )}

                    <div className="project-links">
                      {github && (
                        <motion.a
                          href={github}
                          aria-label="GitHub Link"
                          target="_blank"
                          rel="noreferrer"
                          whileHover={{ scale: 1.1, y: -2 }}
                          whileTap={{ scale: 0.95 }}>
                          <Icon name="GitHub" />
                        </motion.a>
                      )}
                      {external && (
                        <motion.a
                          href={external}
                          aria-label="External Link"
                          className="external"
                          target="_blank"
                          rel="noreferrer"
                          whileHover={{ scale: 1.1, y: -2 }}
                          whileTap={{ scale: 0.95 }}>
                          <Icon name="External" />
                        </motion.a>
                      )}
                    </div>
                  </div>
                </StyledContent>

                <StyledImage
                  className="project-image"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 300 }}>
                  <a href={external || github || '#'}>
                    <GatsbyImage image={image} alt={title} className="img" />
                  </a>
                </StyledImage>
              </StyledProject>
            );
          })}
      </StyledProjectsGrid>
    </StyledSection>
  );
};

export default Featured;
